import React, { Component } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Paper, IconButton, Tooltip, Checkbox } from '@material-ui/core';
import MaterialTable from 'material-table';
import DeleteOutline from '@material-ui/icons/DeleteOutline';

import User from '../../../../models/User';
import Cart from '../../../../models/Cart';
import Awb from '../../../../models/Awb';
import cartRemote from '../../../../api/cartRemote';
import awbRemote from '../../../../api/awbRemote';
import { receiveCart, updateWholeCart } from '../../../../store/actions/cartAction';
import { receiveGhaAwbs } from '../../../../store/actions/awbsAction';
import { receiveError, receiveSuccess } from '../../../../store/actions/notificationAction';
import commonStyles from '../../../common/common.module.css';
import styles from './goCart.module.css';
import PrevNext from '../../../prevAndNext/PrevNext';
import tableIcons from '../../../../utils/editTableIcons';

const TOP_ALIGNED_CELL_STYLE = { verticalAlign: 'top' };

interface CreateGoOrderState {
  loading: boolean;
  deselectedPartsByAwb: { [awbKey: string]: { [partId: string]: boolean } };
}

interface LocationState {
  editOrderNumber?: string;
}

interface CreateGoOrderProps extends RouteComponentProps<{}, any, LocationState> {
  user: User;
  cart: Cart;
  ghaAwbs: Awb[];
  userRole: string;
  type: string;
  getCartItems: () => void;
  getExportAwbs: () => void;
  updateCart: (cart: Cart) => void;
  showError: (error: any) => void;
  showSuccess: (msg: string) => void;
}

class CreateGoOrderContainer extends Component<CreateGoOrderProps, CreateGoOrderState> {
  constructor(props: CreateGoOrderProps) {
    super(props);
    this.state = {
      loading: false,
      deselectedPartsByAwb: {}
    };
  }

  getAwbKey = (awb: any): string => `${awb.code}-${awb.number}-${awb.version || 0}`;

  getPartId = (part: any): string => part.id || `${part.flight_no}-${part.flight_date}`;

  isTrueFlag = (value: any): boolean => value === true || value === 1 || value === '1' || value === 'true'

  hasOrderLink = (part: any): boolean => {
    const hasOrderInfo = !!(part?.order_info && (part.order_info.order_number || part.order_info.delivered_date));
    return hasOrderInfo || this.isTrueFlag(part?.in_order) || !!part?.id_order;
  }

  isDeliveredPart = (part: any): boolean => {
    return this.isTrueFlag(part?.delivered) && this.hasOrderLink(part);
  }

  isInOrderPart = (part: any): boolean => {
    return this.isTrueFlag(part?.in_order);
  }

  isLockedPart = (part: any): boolean => this.isDeliveredPart(part) || this.isInOrderPart(part)

  hasSelectablePart = (awb: any): boolean => {
    const parts = this.getCandidateParts(awb);
    if (!parts.length) {
      return !(awb?.converted_to_po === true || awb?.converted_to_po === 1 || awb?.converted_to_po === '1' || awb?.converted_to_po === 'true');
    }
    return parts.some((part: any) => !this.isLockedPart(part));
  }

  normalizeAwbNumber = (value: any): string => {
    const str = String(value ?? '').trim();
    const normalized = str.replace(/^0+/, '');
    return normalized.length ? normalized : '0';
  }

  isSameAwb = (left: any, right: any): boolean => {
    const leftCode = String(left?.code ?? '').trim().toUpperCase();
    const rightCode = String(right?.code ?? '').trim().toUpperCase();
    const leftNumber = this.normalizeAwbNumber(left?.number);
    const rightNumber = this.normalizeAwbNumber(right?.number);
    return leftCode === rightCode && leftNumber === rightNumber;
  }

  getCartItems = (): any[] => {
    return (this.props.cart as any)?.items || (this.props.cart as any)?.cart || (this.props.cart as any)?.list || [];
  }

  getCartItemForAwb = (awb: any): any | undefined => {
    return this.getCartItems().find((item: any) => this.isSameAwb(item, awb));
  }

  getDisplayPiecesWeight = (item: any): { pieces: number; weight: number } => {
    if (Array.isArray(item.parts) && item.parts.length > 0) {
      return item.parts.reduce((acc: { pieces: number; weight: number }, part: any) => ({
        pieces: acc.pieces + (Number(part.pcs ?? part.pieces) || 0),
        weight: acc.weight + (Number(part.wgt ?? part.weight) || 0)
      }), { pieces: 0, weight: 0 });
    }

    const candidateParts = this.getCandidateParts(item);
    if (candidateParts.length > 1) {
      const selectedParts = candidateParts.filter((part: any) => this.isPartSelected(item, part));
      const partsForDisplay = selectedParts.length > 0
        ? selectedParts
        : candidateParts.filter((part: any) => !this.isLockedPart(part));

      if (partsForDisplay.length > 0) {
        return partsForDisplay.reduce((acc: { pieces: number; weight: number }, part: any) => ({
          pieces: acc.pieces + (Number(part.pcs ?? part.pieces) || 0),
          weight: acc.weight + (Number(part.wgt ?? part.weight) || 0)
        }), { pieces: 0, weight: 0 });
      }
    }

    return {
      pieces: Number(item.pieces) || 0,
      weight: Number(item.weight) || 0
    };
  }

  getCandidateParts = (awb: any): any[] => {
    if (awb.import_parts && awb.import_parts.length) {
      return awb.import_parts;
    }
    if (awb.export_parts && awb.export_parts.length) {
      return awb.export_parts;
    }
    return [];
  }

  isPartSelected = (awb: any, part: any): boolean => {
    if (this.isLockedPart(part)) {
      return false;
    }

    const awbKey = this.getAwbKey(awb);
    const partId = this.getPartId(part);
    const hasLocalSelectionState = Object.prototype.hasOwnProperty.call(this.state.deselectedPartsByAwb, awbKey);

    if (hasLocalSelectionState) {
      return !this.state.deselectedPartsByAwb[awbKey]?.[partId];
    }

    const cartItem = this.getCartItemForAwb(awb);
    if (cartItem && Array.isArray(cartItem.parts) && cartItem.parts.length > 0) {
      return cartItem.parts.some((p: any) => String(p.id) === String(partId));
    }

    const candidateParts = this.getCandidateParts(awb);
    const hasExplicitSelection = candidateParts.some((p: any) => this.isTrueFlag(p?.cart) || this.isTrueFlag(p?.added));
    if (hasExplicitSelection) {
      return this.isTrueFlag(part?.cart) || this.isTrueFlag(part?.added);
    }

    const selectableParts = candidateParts.filter((p: any) => !this.isLockedPart(p));
    if (selectableParts.length > 1) {
      return false;
    }

    return true;
  }

  togglePartSelection = (awb: any, part: any) => {
    const awbKey = this.getAwbKey(awb);
    const partId = this.getPartId(part);
    const currentlySelected = this.isPartSelected(awb, part);

    this.setState(prev => {
      const currentAwb = { ...(prev.deselectedPartsByAwb[awbKey] || {}) };
      if (currentlySelected) {
        currentAwb[partId] = true;
      } else {
        delete currentAwb[partId];
      }

      return {
        deselectedPartsByAwb: {
          ...prev.deselectedPartsByAwb,
          [awbKey]: currentAwb
        }
      };
    });
  }

  async componentDidMount() {
    const editOrderNumber = this.props.location?.state?.editOrderNumber || sessionStorage.getItem('editGoOrderNumber');
    if (editOrderNumber) {
        this.setState({ loading: true });
        try {
            await cartRemote.prepEditGoOrder(editOrderNumber);
        } catch (e) {
            console.error('Failed to prep edit GO order:', e);
        }
        this.setState({ loading: false });
    }

    this.props.getCartItems();
    this.props.getExportAwbs();
  }

  handleRemoveAwb = async (code: string, number: string, isCartItem?: boolean) => {
    try {
      this.setState({ loading: true });
      if (isCartItem) {
        await cartRemote.deleteAwbFromCart(code, number);
        this.props.getCartItems();
      } else {
        await awbRemote.deleteAwb(code, number);
        this.props.getExportAwbs();
      }
    } catch (e) {
      console.error('Failed to remove AWB:', e);
    } finally {
      this.setState({ loading: false });
    }
  }

  handleNext = async () => {
    this.setState({ loading: true });
    try {
      for (const awb of this.props.ghaAwbs) {
        const cartItem = this.getCartItemForAwb(awb);
        const parts = this.getCandidateParts(awb);
        const isPartShipmentAwb = parts.length > 1;

        if (!isPartShipmentAwb) {
          if (!this.hasSelectablePart(awb)) {
            if (cartItem) {
              await cartRemote.deleteAwbFromCart(awb.code, awb.number);
            }
            continue;
          }
          if (cartItem) {
            continue;
          }
          await cartRemote.addAirwayBillToCart(awb.code, awb.number, awb.version || 0);
          continue;
        }

        const selectedPartIds = parts
          .filter((part: any) => !this.isLockedPart(part) && this.isPartSelected(awb, part))
          .map((part: any) => this.getPartId(part));

        if (selectedPartIds.length === 0) {
          if (cartItem) {
            await cartRemote.deleteAwbFromCart(awb.code, awb.number);
          }
          continue;
        }

        if (cartItem) {
          const existingPartIds = new Set((cartItem.parts || []).map((p: any) => String(p.id)));
          const selectedPartIdSet = new Set(selectedPartIds.map((id: string) => String(id)));
          const hasSameSelection = existingPartIds.size === selectedPartIdSet.size
            && selectedPartIds.every((id: string) => existingPartIds.has(String(id)));

          if (hasSameSelection) {
            continue;
          }

          await cartRemote.deleteAwbFromCart(awb.code, awb.number);
        }

        for (const partId of selectedPartIds) {
          await cartRemote.addAirwayBillPartToCart(awb.code, awb.number, awb.version || 0, partId, true);
        }
      }
      
      this.props.history.push('/cart-go');
    } catch (e) {
      console.error('Failed to add to cart:', e);
      this.props.showError(e);
      this.setState({ loading: false });
    }
  }

  handleBack = async () => {
    this.setState({ loading: true });
    try {
      await cartRemote.emptyCart();
    } catch (e) {
      console.error('Failed to empty cart:', e);
    }
    this.setState({ loading: false });
    const editOrderNumber = this.props.location?.state?.editOrderNumber || sessionStorage.getItem('editGoOrderNumber');
    if (editOrderNumber) {
      sessionStorage.removeItem('editGoOrderNumber');
      this.props.history.push(`/go/${editOrderNumber}`);
    } else {
      this.props.history.push('/my-go-orders');
    }
  }

  render() {
    const cartItems = (this.props.cart as any)?.items || (this.props.cart as any)?.cart || (this.props.cart as any)?.list || [];
    const cartItemsMapped = cartItems.map((c: any) => {
      const match = (this.props.ghaAwbs || []).find((awb: any) => this.isSameAwb(awb, c));
      return { 
        ...c, 
        origin: c.origin || match?.origin,
        destination: c.destination || match?.destination,
        isCartItem: true 
      };
    });
    const ghaAwbsFiltered = (this.props.ghaAwbs || []).filter((awb: any) => 
      !cartItemsMapped.some((c: any) => this.isSameAwb(c, awb))
    );
    const mergedAwbs = [ ...cartItemsMapped, ...ghaAwbsFiltered ];
    const hasItems = mergedAwbs.length > 0;

    return (
      <div>
        <div className={ commonStyles.wrapper }>
          <h3 className={ `${ commonStyles.header }` } >Review AWBs</h3>
          <div>
            <MaterialTable
              components={{ Container: (props: any) => <Paper { ...props } elevation={0} className={`${ commonStyles.paperTable } ${ styles.reviewAwbTable }`}/> }}
              icons={ tableIcons }
              data={ hasItems ? mergedAwbs : [] }
              columns={[
                {
                  title: 'AWB Number',
                  cellStyle: TOP_ALIGNED_CELL_STYLE,
                    render: (rowData: any) => {
                      const candidateParts = this.getCandidateParts(rowData) || [];

                      return (
                        <div>
                          <div>{rowData.code}-{rowData.number}</div>
                          {candidateParts.length > 1 && (
                            <div style={{ marginTop: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '6px' }}>
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>Select shipment part(s):</div>
                              {candidateParts.map((part: any, partIdx: number) => {
                                const partId = this.getPartId(part);
                                return (
                                  <label key={partIdx} style={{ display: 'flex', alignItems: 'center', fontSize: '12px', marginBottom: '3px', cursor: 'pointer' }}>
                                    <Checkbox
                                      size="small"
                                      color="primary"
                                      checked={ this.isPartSelected(rowData, part) }
                                      disabled={ this.isLockedPart(part) }
                                      onChange={() => this.togglePartSelection(rowData, part)}
                                      style={{ padding: '2px 6px 2px 0' }}
                                    />
                                    <span>
                                      {partId} ({part.pieces ?? part.pcs ?? 0} pcs, {part.weight ?? part.wgt ?? 0} kg)
                                      {this.isDeliveredPart(part) ? ' - Delivered (cannot be re-added)' : this.isInOrderPart(part) ? ' - Already in order' : ''}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }
                },
                {
                  title: 'Origin',
                  cellStyle: TOP_ALIGNED_CELL_STYLE,
                  render: (rowData: any) => <span>{rowData.origin || '-'}</span>
                },
                {
                  title: 'Destination',
                  cellStyle: TOP_ALIGNED_CELL_STYLE,
                  render: (rowData: any) => <span>{rowData.destination || '-'}</span>
                },
                {
                  title: 'Pieces',
                  cellStyle: TOP_ALIGNED_CELL_STYLE,
                  render: (rowData: any) => <span>{this.getDisplayPiecesWeight(rowData).pieces}</span>
                },
                {
                  title: 'Weight',
                  cellStyle: TOP_ALIGNED_CELL_STYLE,
                  render: (rowData: any) => <span>{this.getDisplayPiecesWeight(rowData).weight} kg</span>
                },
                {
                  title: 'Actions',
                  headerStyle: { textAlign: 'center' },
                  cellStyle: { ...TOP_ALIGNED_CELL_STYLE, textAlign: 'center' },
                  render: (rowData: any) => (
                    <Tooltip title="Remove AWB">
                      <span>
                        <IconButton
                          color="primary"
                          size="small"
                          disabled={this.state.loading}
                          onClick={() => this.handleRemoveAwb(rowData.code, rowData.number, rowData.isCartItem)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                }
              ]}
              options={{
                search: false,
                paging: false,
                showTitle: false,
                toolbar: false,
                sorting: false,
                actionsColumnIndex: -1,
                headerStyle: {
                  backgroundColor: 'var(--tbl_head_bkg)',
                  color: 'var(--tbl_head_color)',
                  zIndex: 2,
                },
              }}
              localization={{
                body: {
                  emptyDataSourceMessage: 'No AWBs added yet.'
                }
              }}
            />
          </div>
        </div>

        <div className={ `${styles.footerActionsSpaceBtw} ${styles.footerButtonWrapper}` }>
          <PrevNext
            prevButton={{
              active: true,
              onClick: this.handleBack,
              animate: true
            }}
          />
          <Button 
            color="primary" 
            variant="contained"
            disabled={!hasItems || this.state.loading}
            onClick={() => this.handleNext()}
            className={ styles.footerNextButton }
          >
            { this.state.loading ? 'Processing...' : (sessionStorage.getItem('editGoOrderNumber') ? 'Review Changes' : 'Review Cart & Checkout') }
          </Button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  user: state.user,
  cart: state.cart,
  ghaAwbs: state.awbsGha
});

const mapDispatchToProps = (dispatch: Function) => ({
  getCartItems: () => dispatch(receiveCart(true)),
  getExportAwbs: () => dispatch(receiveGhaAwbs(true)),
  updateCart: (cart: Cart) => dispatch(updateWholeCart(cart)),
  showError: (error: any) => dispatch(receiveError(error)),
  showSuccess: (msg: string) => dispatch(receiveSuccess(msg))
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateGoOrderContainer);
